import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  invoiceApi, paymentApi, payfastApi, attorneyApi,
  type InvoiceSummary, type Attorney, type AttorneyFirm,
} from '../api';
import Select from '../components/common/Select';

// Invoice list. Full proforma-create flow is matter-driven (see Matter
// detail page \u2192 "Create proforma" action). This page shows every invoice
// across the chambers with status filters, drill-down, and issue / void.

export default function Invoices() {
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [attorneys, setAttorneys] = useState<Attorney[]>([]);
  const [firms, setFirms] = useState<AttorneyFirm[]>([]);
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await invoiceApi.list(status ? { status } : {});
      setInvoices(r.invoices);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Bill-to resolution: fetch attorneys + firms once, look up by ID per row.
  // Keeps the list endpoint cheap (no JOIN) without sacrificing readability.
  useEffect(() => {
    attorneyApi.list().then((r) => setAttorneys(r.attorneys)).catch(() => null);
    attorneyApi.listFirms().then((r) => setFirms(r.firms)).catch(() => null);
  }, []);

  const billToById = useMemo(() => {
    const attMap = new Map(attorneys.map((a) => [String(a.ROWID), a]));
    const firmMap = new Map(firms.map((f) => [String(f.ROWID), f]));
    return (inv: InvoiceSummary): string => {
      const f = inv.billed_firm_id     ? firmMap.get(String(inv.billed_firm_id))     : null;
      const a = inv.billed_attorney_id ? attMap.get(String(inv.billed_attorney_id)) : null;
      if (f && a) return `${f.name} — ${[a.first_name, a.last_name].filter(Boolean).join(' ')}`;
      if (f) return f.name;
      if (a) return [a.first_name, a.last_name].filter(Boolean).join(' ') || `#${a.ROWID}`;
      return '';
    };
  }, [attorneys, firms]);

  const issue = async (inv: InvoiceSummary) => {
    if (!confirm(`Issue ${inv.number} as a tax invoice?`)) return;
    try { await invoiceApi.issue(inv.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };
  const voidIt = async (inv: InvoiceSummary) => {
    if (!confirm(`Void ${inv.number}? This cannot be undone.`)) return;
    try { await invoiceApi.void(inv.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };

  const statusStyle = (s: string): string => {
    if (s === 'paid')           return 'success';
    if (s === 'partially_paid') return 'info';
    if (s === 'issued')         return 'accent';
    if (s === 'overdue')        return 'danger';
    if (s === 'void')           return '';
    return '';
  };

  return (
    <div className="stack">
      <div className="row">
        <h1 className="grow">Invoices</h1>
        <div style={{ width: 200 }}>
          <Select
            value={status}
            onChange={setStatus}
            options={[
              { value: '',               label: 'All statuses' },
              { value: 'draft',          label: 'Draft' },
              { value: 'issued',         label: 'Issued' },
              { value: 'partially_paid', label: 'Partially paid' },
              { value: 'paid',           label: 'Paid' },
              { value: 'overdue',        label: 'Overdue' },
              { value: 'void',           label: 'Void' },
            ]}
          />
        </div>
        <button onClick={load}>Refresh</button>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>Loading\u2026</div>
        ) : invoices.length === 0 ? (
          <p className="muted" style={{ padding: 14 }}>
            No invoices yet. Generate a proforma from a matter \u2192 unbilled time + disbursements, then issue it as a tax invoice here.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Bill to</th>
                <th>Issued</th>
                <th>Due</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th style={{ width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const outstanding = Number(inv.total_zar) - Number(inv.amount_paid_zar || 0);
                const billLabel = billToById(inv);
                return (
                  <tr key={inv.ROWID}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      <Link to={`/invoices/${inv.ROWID}`} style={{ fontWeight: 500 }}>{inv.number}</Link>
                    </td>
                    <td><span className="pill">{inv.kind.replace('_', ' ')}</span></td>
                    <td><span className={`pill ${statusStyle(inv.status)}`}>{inv.status.replace('_', ' ')}</span></td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={billLabel}>
                      {billLabel || <span className="muted">\u2014</span>}
                    </td>
                    <td>{inv.issue_date || <span className="muted">\u2014</span>}</td>
                    <td>{inv.due_date   || <span className="muted">\u2014</span>}</td>
                    <td style={{ textAlign: 'right' }}>R {Number(inv.total_zar).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                    <td style={{ textAlign: 'right' }}>R {Number(inv.amount_paid_zar || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                    <td style={{ textAlign: 'right', color: outstanding > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                      R {outstanding.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Link to={`/invoices/${inv.ROWID}`} style={{ marginRight: 6, fontSize: 13 }}>
                        {inv.status === 'draft' ? 'Edit' : 'View'}
                      </Link>
                      <a
                        href={invoiceApi.pdfUrl(inv.ROWID)}
                        target="_blank"
                        rel="noopener"
                        style={{ marginRight: 6, fontSize: 13 }}
                      >PDF</a>
                      {inv.status !== 'void' && <EmailButton invoice={inv} onSent={load} />}
                      {['issued', 'partially_paid'].includes(inv.status) && outstanding > 0 && (
                        <PayLinkButton invoice={inv} />
                      )}
                      {inv.status === 'draft' && <button onClick={() => issue(inv)} style={{ marginLeft: 6 }}>Issue</button>}
                      {['issued', 'partially_paid'].includes(inv.status) && (
                        <RecordPaymentButton invoice={inv} onDone={load} outstanding={outstanding} />
                      )}
                      {inv.status !== 'void' && inv.status !== 'paid' && (
                        <button onClick={() => voidIt(inv)} style={{ marginLeft: 6 }}>Void</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function EmailButton({ invoice, onSent }: { invoice: InvoiceSummary; onSent: () => void }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setSending(true); setError(null);
    try {
      await invoiceApi.email(invoice.ROWID, { to: to || undefined, message: message || undefined });
      setOpen(false);
      onSent();
    } catch (e) { setError((e as Error).message); }
    finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} style={{ marginLeft: 6 }}>Email</button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setOpen(false)}>
          <div className="card stack" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Email {invoice.number}</h3>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              The invoice PDF will be attached. Leave "To" blank to use the billed attorney's/firm's email on file.
            </p>
            <div>
              <label>To (override)</label>
              <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Optional — attorney@firm.co.za" />
            </div>
            <div>
              <label>Message (optional)</label>
              <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short note to accompany the invoice…" />
            </div>
            {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setOpen(false)} disabled={sending}>Cancel</button>
              <button className="primary" onClick={send} disabled={sending}>
                {sending ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RecordPaymentButton({ invoice, outstanding, onDone }: { invoice: InvoiceSummary; outstanding: number; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(outstanding);
  const [method, setMethod] = useState<'eft'|'card'|'cash'|'trust_transfer'|'other'>('eft');
  const [reference, setReference] = useState('');
  const [received_on, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await paymentApi.create({
        invoice_id: invoice.ROWID,
        amount_zar: Number(amount),
        method, reference,
        received_on,
      });
      setOpen(false);
      onDone();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} style={{ marginLeft: 6 }}>+ Payment</button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setOpen(false)}>
          <div className="card stack" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Record payment against {invoice.number}</h3>
            <div className="row">
              <div className="grow">
                <label>Received on</label>
                <input type="date" value={received_on} onChange={(e) => setReceivedOn(e.target.value)} />
              </div>
              <div style={{ width: 160 }}>
                <label>Amount (ZAR)</label>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
              </div>
            </div>
            <div>
              <label>Method</label>
              <Select
                value={method}
                onChange={(v) => setMethod(v as typeof method)}
                options={[
                  { value: 'eft',            label: 'EFT' },
                  { value: 'card',           label: 'Card' },
                  { value: 'cash',           label: 'Cash' },
                  { value: 'trust_transfer', label: 'Trust transfer' },
                  { value: 'other',          label: 'Other' },
                ]}
              />
            </div>
            <div>
              <label>Reference (optional)</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Bank reference / cheque #" />
            </div>
            {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button className="primary" onClick={save} disabled={saving || amount <= 0}>
                {saving ? 'Saving\u2026' : 'Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// One-click PayFast pay-link generator. Calls /payfast/link, copies the URL
// to the clipboard, and offers an "Open" link so the user can verify it. The
// same link is also auto-embedded in the invoice email when PayFast is set
// up - this button is for when you want to send it out-of-band (Slack/SMS).
function PayLinkButton({ invoice }: { invoice: InvoiceSummary }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const r = await payfastApi.generateLink(invoice.ROWID);
      setLink(r.url);
      try { await navigator.clipboard.writeText(r.url); } catch { /* clipboard denied; link still visible */ }
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === 'payfast_not_configured') {
        setError('Configure PayFast under Settings then Integrations first.');
      } else {
        setError(err.message || 'Could not generate link.');
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={generate} disabled={busy} style={{ marginLeft: 6 }} title="Generate a PayFast payment link">
        {busy ? '...' : 'Pay link'}
      </button>
      {(link || error) && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => { setLink(null); setError(null); }}
        >
          <div className="card stack" style={{ width: 540 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Payment link for {invoice.number}</h3>
            {error ? (
              <div style={{ color: 'var(--color-danger)' }}>{error}</div>
            ) : (
              <>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  Copied to clipboard. The link is unique to this invoice and remains valid until the balance is settled.
                </p>
                <input value={link || ''} readOnly style={{ fontSize: 12 }} onFocus={(e) => e.currentTarget.select()} />
              </>
            )}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setLink(null); setError(null); }}>Close</button>
              {link && <a href={link} target="_blank" rel="noopener noreferrer"><button className="primary">Open</button></a>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
